import { FC, useEffect, useState } from "react";

import styled from "@emotion/styled";
import { APP_BREAKPOINT } from "src/constants/layout";
import translate from "src/lib/translate";
import { tracker } from "src/state/state";
import { TrackEvent } from "src/state/Track/TrackCubit";
import Translate from "src/ui/components/Translate/Translate";

import IconLock from "./IconLock";
import IconTooltipTip from "./IconTooltipTip";
import {
  DAILY_INSIGHT_MEAL_THRESHOLD,
  unlockProgress
} from "./mealInsightHelpers";
import {
  Card,
  Fog,
  Footer,
  Insight,
  InsightWrap,
  Label
} from "./MealTodayInsightCard.styled";

/**
 * Stand-in for the insight the member hasn't earned yet. Blurred past legibility
 * and hidden from assistive tech, so it reads as texture rather than content —
 * which is why it isn't translated.
 */
const PLACEHOLDER_INSIGHT = `Lunch was a strong protein hit, 38g and half-plate veg.

Across the 2 meals you logged, you're at 33g protein. Whatever you eat next, aiming for ≥30g protein will get you to your floor.`;
const PLACEHOLDER_FOOTER = "Updated at 2:04PM · 2 meals logged";

// Desktop gives the blurred preview more room to breathe than the 16px the shell
// applies on mobile.
const LockedCard = styled(Card)`
  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    padding: 1.5rem;
  }
`;

// Mirrors the flex column the unlocked card gets straight from the shell, so the
// placeholder fills the same fixed-height box the real insight does.
const Blurred = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  filter: blur(11px);
`;

const LockOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

// The hint is a hover affordance on pointer devices and a tap toggle on touch,
// so the padlock is the button that owns both.
const LockButton = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5625rem;
  padding: 0;
  border: none;
  background: none;
  color: var(--Greys-Gray, #aaa7a0);
  cursor: pointer;
  pointer-events: auto;

  > svg {
    width: 2.5rem;
    height: 2.5rem;
  }

  &:hover [data-tooltip],
  &:focus-visible [data-tooltip] {
    opacity: 1;
  }
`;

const Tooltip = styled.span`
  position: absolute;
  top: calc(100% + 0.5625rem);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  opacity: 0;
  transition: opacity 0.15s ease;
  margin: 0;
  max-width: 250px;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: var(--color-charcoal, #212121);
  color: var(--color-white, #fff);
  font-size: 0.875rem;
  line-height: 1.25rem;
  letter-spacing: -0.28px;
  text-align: center;

  &[data-visible="true"] {
    opacity: 1;
  }
`;

const TooltipTip = styled.span`
  position: absolute;
  top: -7px;
  left: 50%;
  transform: translateX(-50%) rotate(180deg);
  display: flex;
  width: 16px;
  height: 7.336px;
  color: var(--color-charcoal, #212121);

  svg {
    width: 100%;
    height: 100%;
  }
`;

interface MealTodayLockedCardProps {
  /** Meals logged today, shown as progress towards the unlock threshold. */
  mealCount: number;
}

const MealTodayLockedCard: FC<MealTodayLockedCardProps> = ({ mealCount }) => {
  const [hintOpen, setHintOpen] = useState(false);
  const logged = unlockProgress(mealCount);
  const hint = translate("mealInsights.today.locked.unlock", {
    logged,
    required: DAILY_INSIGHT_MEAL_THRESHOLD
  });

  useEffect(() => {
    tracker.track(TrackEvent.DailyInsightLockedDisplayed);
  }, []);

  const toggleHint = (): void => {
    if (!hintOpen) {
      tracker.track(TrackEvent.DailyInsightLockTooltipOpened);
    }
    setHintOpen((open) => !open);
  };

  return (
    <LockedCard as="div" data-interactive="false">
      <Label>
        <Translate msg="mealInsights.today.insightsLabel" />
      </Label>
      <Blurred aria-hidden>
        <InsightWrap>
          <Insight>{PLACEHOLDER_INSIGHT}</Insight>
          <Fog />
        </InsightWrap>
        <Footer>{PLACEHOLDER_FOOTER}</Footer>
      </Blurred>
      <LockOverlay>
        <LockButton type="button" aria-label={hint} onClick={toggleHint}>
          <IconLock />
          <Tooltip
            data-tooltip
            data-visible={hintOpen ? "true" : "false"}
            aria-hidden
          >
            {hint}
            <TooltipTip>
              <IconTooltipTip />
            </TooltipTip>
          </Tooltip>
        </LockButton>
      </LockOverlay>
    </LockedCard>
  );
};

export default MealTodayLockedCard;
